/* eslint-disable @typescript-eslint/no-explicit-any */

type EChartsOptions = Record<string, any>;

export type DefaultChartConfig = {
    designResolution: { width: number; height: number };
    echarts: EChartsOptions;
};

const DEFAULT_DESIGN_RESOLUTION = { width: 600, height: 600 };

const wrapDefaults = (configs: EChartsOptions[]): DefaultChartConfig[] =>
    configs.map((echarts) => ({ designResolution: DEFAULT_DESIGN_RESOLUTION, echarts }));

const PALETTE: string[] = [
    'themeRef_colors_0',
    'themeRef_colors_1',
    'themeRef_colors_2',
    'themeRef_colors_3',
    'themeRef_colors_4',
    'themeRef_colors_5',
    'themeRef_colors_6',
    'themeRef_colors_7',
];

const PIE_CONFIG_1: EChartsOptions = {
    backgroundColor: 'transparent',
    color: PALETTE,

    series: [
        {
            name: 'Access From',
            type: 'pie',
            radius: ['42%', '72%'],
            center: ['50%', '50%'],
            avoidLabelOverlap: false,
            startAngle: 90,
            animationType: 'expansion',
            animationDuration: 1200,
            animationEasing: 'cubicOut',

            itemStyle: {
                borderColor: '#ffffff',
                borderWidth: 6,
            },

            label: { show: false },
            labelLine: { show: false },

            data: [{ value: 70 }, { value: 30 }],
        },
    ],

    graphic: [
        {
            type: 'group',
            left: 'center',
            top: 'center',
            silent: true,
            children: [
                {
                    type: 'text',
                    top: -50,
                    style: {
                        text: '70%',
                        fontSize: 60,
                        fontWeight: 'bold',
                        fontFamily: 'themeRef_fonts_0',
                        fill: '#111',
                        textAlign: 'center',
                        textVerticalAlign: 'middle',
                        opacity: 0,
                    },
                    keyframeAnimation: {
                        delay: 1200,
                        duration: 500,
                        keyframes: [
                            { percent: 0, style: { opacity: 0 } },
                            { percent: 1, style: { opacity: 1 } },
                        ],
                    },
                },
                {
                    type: 'text',
                    top: 28,
                    style: {
                        text: 'Market Share',
                        fontSize: 28,
                        fontFamily: 'themeRef_fonts_0',
                        fill: '#777',
                        textAlign: 'center',
                        textVerticalAlign: 'middle',
                        opacity: 0,
                    },
                    keyframeAnimation: {
                        delay: 1400,
                        duration: 500,
                        keyframes: [
                            { percent: 0, style: { opacity: 0 } },
                            { percent: 1, style: { opacity: 1 } },
                        ],
                    },
                },
            ],
        },
    ],
};

const PIE_CONFIG_2: EChartsOptions = {
    backgroundColor: 'transparent',
    color: PALETTE,

    title: {
        text: 'US Beverage\nMarket',
        left: 'center',
        top: '50%',
        textVerticalAlign: 'middle',
        textStyle: {
            fontSize: 28,
            fontWeight: 'bold',
            fontFamily: 'themeRef_fonts_0',
            lineHeight: 28,
            color: '#111',
        },
    },

    series: [
        {
            type: 'pie',
            radius: ['42%', '72%'],
            center: ['50%', '50%'],
            startAngle: 90,
            animationType: 'expansion',
            animationDuration: 1200,
            animationEasing: 'cubicOut',
            silent: true,

            itemStyle: {
                borderColor: '#ffffff',
                borderWidth: 5,
            },

            label: {
                show: true,
                position: 'outside',
                fontSize: 28,
                fontWeight: 'bold',
                fontFamily: 'themeRef_fonts_0',
                color: 'themeRef_colors_0',
                formatter: '{b}',
                overflow: 'none',
            },

            labelLine: {
                show: true,
                length: 20,
                length2: 14,
            },

            data: [
                {
                    value: 10,
                    name: 'Functional drinks',
                    itemStyle: { color: 'themeRef_colors_0' },
                },
                {
                    value: 90,
                    name: 'Others',
                    itemStyle: { color: '#E5E7EB' },
                    labelLine: { show: false },
                    label: { show: false },
                },
            ],
        },

        {
            type: 'pie',
            radius: ['42%', '72%'],
            center: ['50%', '50%'],
            silent: true,

            label: {
                show: true,
                position: 'inside',
                formatter: '{c}%',
                color: '#fff',
                fontWeight: 'bold',
                fontSize: 28,
                fontFamily: 'themeRef_fonts_0',
            },

            labelLine: { show: false },

            data: [
                { value: 10, name: 'Functional drinks' },
                { value: 90, name: 'Others', label: { show: false } },
            ],
        },
    ],
};

const PIE_CONFIG_3: EChartsOptions = {
    backgroundColor: 'transparent',
    color: PALETTE,

    tooltip: { trigger: 'item' },

    series: [
        {
            type: 'pie',
            radius: '62%',
            center: ['50%', '52%'],
            avoidLabelOverlap: true,

            data: [
                { value: 1048, name: 'Search Engine' },
                { value: 735, name: 'Direct' },
                { value: 580, name: 'Email' },
                { value: 484, name: 'Union Ads' },
                { value: 300, name: 'Video Ads' },
            ],

            animationType: 'expansion',
            animationDuration: 1400,
            animationEasing: 'cubicOut',

            itemStyle: {
                borderColor: '#ffffff',
                borderWidth: 4,
            },

            label: {
                show: true,
                fontSize: 28,
                fontFamily: 'themeRef_fonts_0',
                fontWeight: 'bold',
                color: '#222',
                formatter: '{b}\n{d}%',
                overflow: 'none',
            },

            labelLine: {
                show: true,
                length: 18,
                length2: 14,
            },
        },
    ],
};

const PIE_CONFIG_4: EChartsOptions = {
    backgroundColor: 'transparent',
    color: PALETTE,

    tooltip: { trigger: 'item' },

    title: {
        text: 'Annual sales',
        left: 'center',
        top: '50%',
        textVerticalAlign: 'middle',
        textStyle: {
            fontSize: 28,
            fontWeight: 'bold',
            lineHeight: 28,
            fontFamily: 'themeRef_fonts_0',
            color: '#222',
        },
    },

    series: [
        {
            type: 'pie',
            radius: ['42%', '68%'],
            center: ['50%', '50%'],
            avoidLabelOverlap: true,

            data: [
                { value: 1048, name: 'Search Engine' },
                { value: 735, name: 'Direct' },
                { value: 580, name: 'Email' },
                { value: 484, name: 'Union Ads' },
                { value: 300, name: 'Video Ads' },
            ],

            animationType: 'expansion',
            animationDuration: 1400,
            animationEasing: 'cubicOut',

            itemStyle: {
                borderColor: '#ffffff',
                borderWidth: 4,
            },

            label: {
                show: true,
                fontSize: 28,
                fontFamily: 'themeRef_fonts_0',
                fontWeight: 'bold',
                color: '#222',
                formatter: '{d}%',
                overflow: 'none',
            },

            labelLine: {
                show: true,
                length: 20,
                length2: 16,
            },
        },
    ],
};

export const DEFAULT_PIE_CHART_CONFIGS: DefaultChartConfig[] = wrapDefaults([
    PIE_CONFIG_1,
    PIE_CONFIG_2,
    PIE_CONFIG_3,
    PIE_CONFIG_4,
]);

const BAR_CONFIG_1: EChartsOptions = {
    backgroundColor: 'transparent',
    color: PALETTE,

    grid: {
        left: '6%',
        right: '6%',
        top: '14%',
        bottom: '12%',
        containLabel: true,
    },

    xAxis: {
        type: 'category',
        data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
            fontSize: 28,
            fontFamily: 'themeRef_fonts_0',
            fontWeight: 'bold',
            color: 'themeRef_colors_0',
            margin: 14,
        },
    },

    yAxis: { show: false },

    series: [
        {
            type: 'bar',
            data: [120, 200, 150, 80, 70, 110, 130],
            barWidth: '58%',
            itemStyle: {
                borderRadius: [10, 10, 0, 0],
            },
            label: {
                show: true,
                position: 'top',
                formatter: '{c}%',
                fontSize: 28,
                fontFamily: 'themeRef_fonts_0',
                fontWeight: 'bold',
                color: 'themeRef_colors_0',
                distance: 10,
            },
            animationDuration: 900,
            animationEasing: 'cubicOut',
            animationDelay: (idx: number) => idx * 220,
        },
    ],
};

const BAR_CONFIG_2: EChartsOptions = {
    backgroundColor: 'transparent',
    color: PALETTE,

    grid: {
        left: '12%',
        right: '12%',
        top: '12%',
        bottom: '12%',
        containLabel: true,
    },

    xAxis: {
        type: 'value',
        show: false,
    },

    yAxis: {
        type: 'category',
        data: ['Mar', 'Feb', 'Jan'],
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
            fontSize: 28,
            fontFamily: 'themeRef_fonts_0',
            fontWeight: 'bold',
            color: 'themeRef_colors_0',
            margin: 16,
        },
    },

    series: [
        {
            type: 'bar',
            data: [120, 200, 150],
            barWidth: '55%',
            itemStyle: {
                borderRadius: [0, 10, 10, 0],
            },
            label: {
                show: true,
                position: 'right',
                formatter: '{c}%',
                fontSize: 28,
                fontFamily: 'themeRef_fonts_0',
                fontWeight: 'bold',
                color: 'themeRef_colors_0',
                distance: 10,
            },
            animationDuration: 900,
            animationEasing: 'cubicOut',
            animationDelay: (idx: number) => idx * 220,
        },
    ],
};

const BAR_CONFIG_3_MAX = 200;

const BAR_CONFIG_3: EChartsOptions = {
    backgroundColor: 'transparent',
    color: PALETTE,

    grid: {
        left: '12%',
        right: '12%',
        top: '12%',
        bottom: '12%',
        containLabel: true,
    },

    xAxis: {
        type: 'value',
        show: false,
        max: BAR_CONFIG_3_MAX,
    },

    yAxis: {
        type: 'category',
        data: ['Mar', 'Feb', 'Jan'],
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
            fontSize: 28,
            fontFamily: 'themeRef_fonts_0',
            fontWeight: 'bold',
            color: 'themeRef_colors_0',
            margin: 16,
        },
    },

    series: [
        {
            type: 'bar',
            data: [BAR_CONFIG_3_MAX, BAR_CONFIG_3_MAX, BAR_CONFIG_3_MAX],
            barWidth: '55%',
            itemStyle: {
                color: '#E5E7EB',
                borderRadius: [0, 10, 10, 0],
            },
            silent: true,
            barGap: '-100%',
            _skipDataMerge: true,
        },
        {
            type: 'bar',
            data: [120, 200, 150],
            barWidth: '55%',
            itemStyle: {
                borderRadius: [0, 10, 10, 0],
                color: 'themeRef_colors_0',
            },
            label: {
                show: true,
                position: 'right',
                formatter: '{c}%',
                fontSize: 28,
                fontFamily: 'themeRef_fonts_0',
                fontWeight: 'bold',
                color: 'themeRef_colors_0',
                distance: 10,
            },
            animationDuration: 1000,
            animationEasing: 'cubicOut',
            animationDelay: (idx: number) => idx * 220,
        },
    ],
};

export const DEFAULT_BAR_CHART_CONFIGS: DefaultChartConfig[] = wrapDefaults([
    BAR_CONFIG_1,
    BAR_CONFIG_2,
    BAR_CONFIG_3,
]);

const LINE_CONFIG_1: EChartsOptions = {
    backgroundColor: 'transparent',
    color: PALETTE,

    title: {
        text: 'Line Chart',
        left: 'center',
        top: '3%',
        textStyle: {
            fontSize: 22,
            fontWeight: 'bold',
            fontFamily: 'themeRef_fonts_0',
        },
    },

    tooltip: { trigger: 'axis' },

    grid: {
        left: '6%',
        right: '20%',
        top: '18%',
        bottom: '10%',
        containLabel: true,
    },

    xAxis: {
        type: 'category',
        boundaryGap: false,
        data: ['1985', '1990', '2000', '2005', '2010', '2015', '2020'],
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
            fontSize: 14,
            fontFamily: 'themeRef_fonts_0',
            color: '#666',
        },
    },

    yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
            fontSize: 13,
            fontFamily: 'themeRef_fonts_0',
            color: '#888',
        },
        splitLine: {
            lineStyle: { opacity: 0.15 },
        },
    },

    series: [
        {
            name: 'Email',
            type: 'line',
            smooth: 0.25,
            showSymbol: false,
            lineStyle: { width: 3 },
            endLabel: {
                show: true,
                formatter: '{a}',
                fontWeight: 'bold',
                fontSize: 14,
                fontFamily: 'themeRef_fonts_0',
                color: 'themeRef_colors_0',
            },
            labelLayout: { moveOverlap: 'shiftY' },
            animationDuration: 1800,
            data: [120, 132, 101, 134, 90, 230, 210],
        },
        {
            name: 'Union Ads',
            type: 'line',
            smooth: 0.25,
            showSymbol: false,
            lineStyle: { width: 3 },
            endLabel: {
                show: true,
                formatter: '{a}',
                fontWeight: 'bold',
                fontSize: 14,
                fontFamily: 'themeRef_fonts_0',
                color: 'themeRef_colors_1',
            },
            labelLayout: { moveOverlap: 'shiftY' },
            animationDuration: 1800,
            data: [220, 182, 191, 234, 290, 330, 310],
        },
        {
            name: 'Video Ads',
            type: 'line',
            smooth: 0.25,
            showSymbol: false,
            lineStyle: { width: 3 },
            endLabel: {
                show: true,
                formatter: '{a}',
                fontWeight: 'bold',
                fontSize: 14,
                fontFamily: 'themeRef_fonts_0',
                color: 'themeRef_colors_2',
            },
            labelLayout: { moveOverlap: 'shiftY' },
            animationDuration: 1800,
            data: [150, 232, 201, 154, 190, 330, 410],
        },
        {
            name: 'Direct',
            type: 'line',
            smooth: 0.25,
            showSymbol: false,
            lineStyle: { width: 3 },
            endLabel: {
                show: true,
                formatter: '{a}',
                fontWeight: 'bold',
                fontSize: 14,
                fontFamily: 'themeRef_fonts_0',
                color: 'themeRef_colors_3',
            },
            labelLayout: { moveOverlap: 'shiftY' },
            animationDuration: 1800,
            data: [320, 332, 301, 334, 390, 330, 320],
        },
        {
            name: 'Search Engine',
            type: 'line',
            smooth: 0.25,
            showSymbol: false,
            lineStyle: { width: 4 },
            endLabel: {
                show: true,
                formatter: '{a}',
                fontWeight: 'bold',
                fontSize: 14,
                fontFamily: 'themeRef_fonts_0',
                color: 'themeRef_colors_4',
            },
            labelLayout: { moveOverlap: 'shiftY' },
            animationDuration: 1800,
            data: [820, 932, 901, 934, 1290, 1330, 1320],
        },
    ],
};

const LINE_CONFIG_2: EChartsOptions = {
    backgroundColor: 'transparent',
    color: PALETTE,

    grid: {
        left: '8%',
        right: '8%',
        top: '10%',
        bottom: '10%',
        containLabel: true,
    },

    xAxis: {
        type: 'category',
        boundaryGap: false,
        data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
            fontSize: 14,
            color: '#666',
        },
    },

    yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
            fontSize: 12,
            color: '#999',
        },
        splitLine: {
            lineStyle: { color: 'rgba(0,0,0,0.08)' },
        },
    },

    series: [
        {
            type: 'line',
            name: 'Video Ads',
            data: [820, 932, 901, 934, 1290, 1330, 1320],

            smooth: 0.1,
            showSymbol: false,

            lineStyle: { width: 4 },

            areaStyle: { opacity: 0.15 },

            animationDuration: 2000,
            animationEasing: 'cubicOut',

            endLabel: {
                show: true,
                formatter: '{a}',
                fontSize: 16,
                fontWeight: 'bold',
                fontFamily: 'themeRef_fonts_0',
                color: 'themeRef_colors_0',
            },
        },
    ],
};

export const DEFAULT_LINE_CHART_CONFIGS: DefaultChartConfig[] = wrapDefaults([
    LINE_CONFIG_1,
    LINE_CONFIG_2,
]);

export type ChartSection = {
    label: string;
    chartType: string;
    configs: DefaultChartConfig[];
};

// Canonical chart groups used by both the Elements > Chart tab and the chart settings panel.
export const CHART_SECTIONS: ChartSection[] = [
    { label: 'Line chart', chartType: 'line', configs: DEFAULT_LINE_CHART_CONFIGS },
    { label: 'Bar chart', chartType: 'bar', configs: DEFAULT_BAR_CHART_CONFIGS },
    { label: 'Pie chart', chartType: 'pie', configs: DEFAULT_PIE_CHART_CONFIGS },
];

// Tiles rendered in the scene selectors when a user picks a chart scene to insert/swap/overlay.
// One tile per chart type, using the first default config for the preview.
export const CHART_SCENE_TILES: { chartType: string; config: DefaultChartConfig }[] = [
    { chartType: 'line', config: DEFAULT_LINE_CHART_CONFIGS[0] },
    { chartType: 'bar', config: DEFAULT_BAR_CHART_CONFIGS[0] },
    { chartType: 'pie', config: DEFAULT_PIE_CHART_CONFIGS[0] },
];
